import NotificationsActive from "@mui/icons-material/NotificationsActive";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { GridFilterModel } from "@mui/x-data-grid-pro";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { ViolatedDevices, ViolationsByRule } from "../hooks/useAlertEvaluation";
import AlertRulesDialog from "./AlertRulesDialog";
import { useDataGridSettings } from "@/components/DataGridSettingsProvider";
import { PresetSelect } from "@/components/filter/PresetSelect";
import VehicleFilterButton from "@/components/filter/VehicleFilterButton";
import { ViewSelect } from "@/components/filter/ViewSelect";
import DeviceFilterGrid from "@/components/input/DeviceFilterGrid";
import { useOptionalViewContext } from "@/components/ViewProvider";
import { SavedFilterRef } from "@/hooks/useFocusGridRow";
import { DeviceStatusOverviewData } from "@/models/DeviceStatusOverviewData";

interface FilterToolbarProps {
  filterModel: GridFilterModel;
  setFilterModel: Dispatch<SetStateAction<GridFilterModel>>;
  deviceStats: {
    drivingDevices: number;
    chargingDevices: number;
    chargeInterruptDevices: number;
    notChargingDevices: number;
    withStatusDevices: number;
    withPMDevices: number;
  };
  violationsByRule: ViolationsByRule;
  violatedDevices: ViolatedDevices;
  data: DeviceStatusOverviewData[];
  savedFilterRef?: SavedFilterRef;
}

const FilterToolbar: React.FC<FilterToolbarProps> = ({
  deviceStats,
  filterModel,
  setFilterModel,
  violationsByRule,
  violatedDevices,
  data,
  savedFilterRef,
}: FilterToolbarProps) => {
  const theme = useTheme();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallestScreen = useMediaQuery(theme.breakpoints.down(500));
  const [openDialog, setOpenDialog] = useState(false);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);

  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => setOpenDialog(false);

  const viewMode = !!useOptionalViewContext();
  const { showArchivedVehicles, setShowArchivedVehicles, pageType } = useDataGridSettings();
  let totalViolations = 0;
  for (const set of violationsByRule.values()) totalViolations += set.size;

  const violatedDeviceNames = useMemo(() => {
    const names: string[] = [];
    for (const row of data) {
      if (violatedDevices.has(row.device.id)) names.push(row.device.name);
    }
    return names;
  }, [data, violatedDevices]);

  return (
    <Box
      display="flex"
      flexDirection={isSmallestScreen ? "column" : "row"}
      alignItems="center"
      gap="15px"
      width="100%"
    >
      {!isSmallScreen && (
        <Box
          sx={{
            flex: 1,
            minWidth: pageType === "diagnosticMonitor" ? "900px" : "600px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <DeviceFilterGrid
            {...deviceStats}
            pageType={pageType}
            filterModel={filterModel}
            setFilterModel={setFilterModel}
            activeAlertCount={violatedDevices.size}
            violatedDeviceNames={violatedDeviceNames}
          />
        </Box>
      )}
      <Box sx={{ display: "flex", flexDirection: "row", gap: 2, alignItems: "center" }}>
        <Button
          variant="outlined"
          onClick={() => setAlertDialogOpen(true)}
          sx={{ height: "40px" }}
        >
          <Badge badgeContent={totalViolations} color="error">
            <NotificationsActive />
          </Badge>
        </Button>
        <VehicleFilterButton
          showArchived={showArchivedVehicles}
          onFilterChange={setShowArchivedVehicles}
        />
        {isSmallScreen && (
          <Box sx={{ width: "40%", my: "auto" }}>
            <Button variant="contained" onClick={handleOpenDialog}>
              {"Filter"}
            </Button>
            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="md">
              <DialogTitle>{"Filter Options"}</DialogTitle>
              <DialogContent>
                <DeviceFilterGrid
                  {...deviceStats}
                  pageType={pageType}
                  filterModel={filterModel}
                  setFilterModel={setFilterModel}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog}>{"Close"}</Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", flex: 1, gap: 1 }}>
        {viewMode ? <ViewSelect /> : <PresetSelect />}
      </Box>
      <AlertRulesDialog
        open={alertDialogOpen}
        onClose={() => setAlertDialogOpen(false)}
        violationsByRule={violationsByRule}
        data={data}
        savedFilterRef={savedFilterRef}
      />
    </Box>
  );
};

export default FilterToolbar;
